import React, { useEffect } from "react";
import { CircularProgress } from "@material-ui/core";
import { HistoryCard, HistoryList, HistoryMeta, HistoryRevertBtn, HistoryText } from "../styles";
import { LABELS } from "../labels";
import { useTextHistory } from "../useTextHistory";
import type { TextRegistryScope } from "../types";

interface Props {
	entryKey: string;
	scope: TextRegistryScope;
	onRevert: (versionId: number) => void;
}

export default function DrawerHistoryTab({ entryKey, scope, onRevert }: Props) {
	const { loadHistory, getHistory } = useTextHistory(scope);
	const { entries, loading, error } = getHistory(entryKey);

	useEffect(() => {
		if (entryKey) {
			void loadHistory(entryKey);
		}
	}, [entryKey, scope, loadHistory]);

	function formatDate(iso: string): string {
		try {
			return new Date(iso).toLocaleString("en-GB", {
				day: "2-digit",
				month: "short",
				year: "numeric",
				hour: "2-digit",
				minute: "2-digit",
			});
		} catch {
			return iso;
		}
	}

	if (loading) {
		return (
			<div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
				<CircularProgress size={24} />
			</div>
		);
	}

	if (error) {
		return <p style={{ color: "#dc2626", fontSize: "0.875rem" }}>{error}</p>;
	}

	if (entries.length === 0) {
		return <p style={{ color: "#94a3b8", fontSize: "0.875rem" }}>{LABELS.historyNoVersions}</p>;
	}

	return (
		<HistoryList>
			{entries.map((h) => (
				<HistoryCard key={h.versionId}>
					<HistoryMeta>
						v{h.versionId} &nbsp;·&nbsp; {formatDate(h.publishedAt)}
					</HistoryMeta>
					<HistoryText>{h.entry.currentText || <em style={{ color: "#94a3b8" }}>empty</em>}</HistoryText>
					<HistoryRevertBtn
						onClick={() => {
							if (window.confirm(LABELS.historyRevertConfirm)) {
								onRevert(h.versionId);
							}
						}}>
						{LABELS.historyRevert}
					</HistoryRevertBtn>
				</HistoryCard>
			))}
		</HistoryList>
	);
}
